import {
  Box,
  Button,
  ButtonToggle,
  Flex,
  Link,
  Loading,
  Modal,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { Table } from '@iheartjane/reefer-table';
import { pluralize } from '@iheartjane/utils';
import { flexRender } from '@tanstack/react-table';
import type { ColumnFilter, ReactTable, RowData } from '@tanstack/react-table';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { idConversions } from '../util/idConversions';
import { getSelectedProductIds } from './productSelect.util';
import type { ProductSelectFeatures } from './productSelectFeatures';
import { ProductSelectFilter } from './productSelectFilter';

import styles from './productSelectModal.module.css';

type ProductSearchMode = 'productName' | 'productId';

export interface ProductSelectModalProps<R extends RowData> {
  /**
   * Every id matching the current filters, so "Select all" covers more than the
   * fetched pages. Omit when the consumer can't resolve them.
   */
  allProductIds?: string[];
  /** Hides "Select all" for consumers that can't resolve every match */
  canSelectAll?: boolean;
  /** Option to only display products, hiding filters, selection and save */
  displayOnly?: boolean;
  /** Option to show search mode toggle */
  dualSearchMode?: boolean;
  /** If first column on table should be frozen when scrolling horizontally */
  freezeFirstColumn?: boolean;
  /** Freezes the name column alongside the checkbox. Needs `freezeFirstColumn` */
  freezeNameColumn?: boolean;
  /** Option to show "Review Selected" toggle in header */
  hasReviewMode?: boolean;
  /** Rendered above the search and filters, for consumer-specific controls */
  headerContent?: ReactNode;
  /** What the product search matches, defaults to "Search product name" */
  searchPlaceholder?: string;
  /** Widens the product search and trims its padding, for a longer placeholder */
  searchWidth?: number | string;
  /**
   * Fires when review mode opens or closes, for a consumer whose rows differ
   * between browsing and reviewing. Pair with `reviewMode` to control it.
   */
  onReviewModeChange?: (reviewMode: boolean) => void;
  /** Makes review mode controlled by the consumer */
  reviewMode?: boolean;
  /** Boolean to check if fetching next page of products */
  isFetchingNextPage: boolean;
  /** Boolean to show a loading state in filter popover */
  isLoadingFilters?: boolean;
  /** Boolean to show a loading state in modal */
  isLoadingProducts?: boolean;
  loadingMessage?: ReactNode;
  /** Action fired when next page is requested */
  onNextPage: () => void;
  /** Action fired on save click */
  onSaveClick: () => void;
  /** Product table to be rendered */
  productTable: ReactTable<ProductSelectFeatures, R>;
  /** Optional setState if using dual search mode to set product ids for search */
  setSearchIds?: (ids: string[]) => void;
  subtitle?: string;
  /** Optional title, defaults to "Select products" */
  title?: string;
}

export const ProductSelectModal = <R extends RowData>({
  allProductIds,
  canSelectAll = true,
  displayOnly,
  dualSearchMode,
  freezeFirstColumn,
  freezeNameColumn,
  hasReviewMode,
  headerContent,
  onReviewModeChange,
  isFetchingNextPage,
  isLoadingFilters,
  isLoadingProducts,
  loadingMessage,
  onNextPage,
  onSaveClick,
  productTable,
  reviewMode: controlledReviewMode,
  searchPlaceholder = 'Search product name',
  searchWidth,
  setSearchIds,
  subtitle,
  title = 'Select products',
}: ProductSelectModalProps<R>) => {
  const [searchMode, setSearchMode] = useState('productName');
  const [ids, setIds] = useState<string>('');

  const wrapperRef = useRef(null);
  const { ref } = useInView({
    root: wrapperRef.current,
    threshold: 0,
    onChange: (inView) => {
      if (inView) {
        onNextPage();
      }
    },
    skip: isFetchingNextPage,
  });

  const noResults = productTable.getRowModel().rows.length === 0;
  const productNameColumn = productTable.getColumn('name');

  const productFilters = productTable
    .getVisibleLeafColumns()
    .filter((col) => col.getCanFilter());

  const onToggleSearchMode = (mode: ProductSearchMode) => {
    setSearchMode(mode);
    productTable.resetColumnFilters();
    setSearchIds && setSearchIds([]);
  };

  const onProductIdSearchSubmit = () => {
    const productIds = idConversions.fromStrings(ids);
    setSearchIds && setSearchIds(productIds);
  };

  const selectedIds = new Set(
    Object.entries(productTable.state.rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id)
  );
  const selectedProductsCount = selectedIds.size;
  const [uncontrolledReviewMode, setUncontrolledReviewMode] = useState(false);
  const reviewMode = controlledReviewMode ?? uncontrolledReviewMode;
  const [initialFilters, setInitialFilters] = useState<ColumnFilter[]>([]);
  const reviewModeLabel = reviewMode
    ? 'Select more'
    : `Review selected (${selectedProductsCount})`;

  const changeReviewMode = () => {
    if (!reviewMode) {
      setInitialFilters(productTable.state.columnFilters);
      productTable.resetColumnFilters();
    } else {
      productTable.setColumnFilters(initialFilters);
    }

    setUncontrolledReviewMode(!reviewMode);
    onReviewModeChange && onReviewModeChange(!reviewMode);
  };

  const visibleRows = reviewMode
    ? productTable.getSelectedRowModel().rows
    : productTable.getRowModel().rows;

  return (
    <>
      <Modal.Header
        title={title}
        subtitle={subtitle}
        actions={
          !displayOnly && (
            <>
              {hasReviewMode && (
                <Button
                  variant="secondary"
                  label={reviewModeLabel}
                  mr={16}
                  onClick={changeReviewMode}
                />
              )}
              <Button label="Save" onClick={onSaveClick} />
            </>
          )
        }
      />
      {/* Always mounted — unmounting on a refetch would wipe what was typed */}
      <Box pt={40} pb={24} px={40}>
        {headerContent}
        {dualSearchMode && (
          <Box width="fit-content" pb={24}>
            <ButtonToggle
              className={styles['buttonToggle']}
              value={searchMode}
              onChange={(mode) => onToggleSearchMode(mode as ProductSearchMode)}
              full={false}
            >
              <ButtonToggle.Button
                label="Select products"
                value="productName"
              />
              <ButtonToggle.Button label="By product id" value="productId" />
            </ButtonToggle>
          </Box>
        )}
        <Flex
          flexWrap="wrap"
          gap={16}
          alignItems="center"
          position="sticky"
          top={0}
          zIndex={15}
          id="product-select-filters"
          background="grays-white"
        >
          {searchMode === 'productName' ? (
            <>
              <SearchField
                className={searchWidth ? styles['searchField'] : undefined}
                width={displayOnly ? '100%' : searchWidth}
                borderRadius="sm"
                label={searchPlaceholder}
                name="product-name-search-field"
                placeholder={searchPlaceholder}
                onChange={(value) => productNameColumn?.setFilterValue(value)}
                disableMobileInputStyling
              />
              {!displayOnly && (
                <>
                  {productFilters.map((col) => (
                    <ProductSelectFilter<R>
                      key={col.id}
                      column={col}
                      filterOptions={
                        Array.isArray(col.columnDef.meta?.filterValues)
                          ? col.columnDef.meta?.filterValues
                          : []
                      }
                      isLoading={isLoadingFilters}
                    />
                  ))}
                  <Link onClick={() => productTable.resetColumnFilters()}>
                    Clear filters
                  </Link>
                </>
              )}
            </>
          ) : (
            <Flex gap={16} alignItems="center">
              <SearchField
                label="Search by product ids"
                name="products-ids-search-field"
                autocomplete="off"
                placeholder="Search by product ids"
                defaultValue={ids}
                autoFocus
                labelHidden
                onChange={(productIds) => setIds(productIds)}
                width={416}
                disableMobileInputStyling
              />
              <Button
                variant="secondary"
                label="Search"
                onClick={onProductIdSearchSubmit}
              />
            </Flex>
          )}
        </Flex>
      </Box>
      <Modal.Content ref={wrapperRef} padding={false}>
        {!noResults ? (
          <Table
            className={
              freezeNameColumn ? styles['freezeNameColumn'] : undefined
            }
            freezeFirstColumn={freezeFirstColumn || freezeNameColumn}
            style={{
              height: 'unset',
              // Frozen columns stick to the scrollport, so the gutter moves
              // onto the edge cells instead of sitting on the table
              padding: freezeNameColumn ? '0px' : '0px 40px',
              width: '100%',
            }}
          >
            <Table.Head>
              {productTable.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeaderCell
                      key={header.id}
                      width={`${header.getSize()}px`}
                    >
                      {!header.isPlaceholder &&
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              {visibleRows.map((row, i) => (
                <tr
                  className={clsx(styles['tableRow'], {
                    [styles['selectable']]: !displayOnly,
                  })}
                  onClick={row.getToggleSelectedHandler()}
                  key={row.id}
                  ref={i === visibleRows.length - 1 ? ref : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </tr>
              ))}
            </Table.Body>
          </Table>
        ) : (
          <Box
            width="100%"
            height={`calc(100% - ${isLoadingProducts ? '48px' : '24px'})`}
            mt={isLoadingProducts ? 24 : 0}
            mb={24}
            px={40}
          >
            <Flex
              height="100%"
              alignItems="center"
              justifyContent="center"
              border="grays-light"
              borderRadius="sm"
              position="relative"
            >
              {isLoadingProducts ? (
                <Flex alignItems="center" flexDirection="column">
                  <Loading
                    className={styles['loading']}
                    color="purple"
                    size="lg"
                  />
                  {loadingMessage}
                </Flex>
              ) : (
                <Typography variant="h2-semibold">
                  No products to display
                </Typography>
              )}
            </Flex>
          </Box>
        )}
      </Modal.Content>
      {!displayOnly && (
        <Modal.Footer>
          <Flex justifyContent="space-between" alignItems="center">
            <Typography color="grays-mid">
              {`${pluralize({
                number: selectedProductsCount,
                noun: 'product',
              })} selected`}
            </Typography>
            <Flex gap={16}>
              {canSelectAll && (
                <Button
                  label="Select all"
                  variant="tertiary"
                  // Adds to the selection rather than replacing it, so rows
                  // hidden by the filters stay selected
                  onClick={() =>
                    allProductIds
                      ? productTable.setRowSelection((current) => ({
                          ...current,
                          ...getSelectedProductIds(allProductIds),
                        }))
                      : productTable.toggleAllRowsSelected(true)
                  }
                  disabled={
                    (allProductIds
                      ? allProductIds.every((id) => selectedIds.has(id))
                      : productTable.getIsAllRowsSelected()) || noResults
                  }
                />
              )}
              <Button
                label="Clear all selected"
                variant="tertiary"
                // Clears outright: toggling all rows off only reaches loaded
                // rows, leaving ids from unfetched pages selected
                onClick={() => productTable.setRowSelection({})}
                disabled={!selectedProductsCount}
              />
            </Flex>
          </Flex>
        </Modal.Footer>
      )}
    </>
  );
};
